import React, { useRef, useState } from 'react';
import { Pin, Trash2, Paperclip, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminCommunityTab({ communityMessages, onRefresh }) {
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum 50MB allowed');
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result);
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      setFilePreview('video');
    } else {
      setFilePreview('document');
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    const token = localStorage.getItem('token');
    let fileData = null;

    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadResponse = await fetch(`${API}/messages/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (!uploadResponse.ok) throw new Error('Failed to upload file');
        fileData = await uploadResponse.json();
      }

      const response = await fetch(`${API}/community/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessage.trim() || 'Shared a file',
          file_url: fileData ? fileData.filename : null,
          file_type: fileData ? fileData.file_type : null,
          file_name: fileData ? fileData.original_name : null
        })
      });

      if (response.ok) {
        setNewMessage('');
        clearFile();
        onRefresh();
        toast.success('Posted to community!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to post');
    } finally {
      setSending(false);
    }
  };

  const handlePin = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/community/messages/${messageId}/pin`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onRefresh();
        toast.success('Message pinned/unpinned');
      }
    } catch (error) {
      toast.error('Failed to pin message');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this community post?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/community/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onRefresh();
        toast.success('Post deleted');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  return (
    <>
      <h2 className='text-2xl font-bold text-[#1A3C34] mb-6' style={{ fontFamily: 'Clash Display' }}>Community Management</h2>
      
      <Card className='bg-white rounded-2xl p-6 mb-6'>
        <h3 className='text-lg font-bold text-[#1A3C34] mb-4'>Post to Community</h3>
        <form onSubmit={handlePost}>
          {selectedFile && (
            <div className='mb-3 p-3 bg-gray-100 rounded-lg flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {filePreview && filePreview !== 'video' && filePreview !== 'document' && (
                  <img src={filePreview} alt='Preview' className='w-12 h-12 object-cover rounded' />
                )}
                <div>
                  <p className='text-sm font-medium text-gray-700'>{selectedFile.name}</p>
                  <p className='text-xs text-gray-500'>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <Button type='button' onClick={clearFile} variant='ghost' size='sm' className='text-red-500'>
                <X className='w-4 h-4' />
              </Button>
            </div>
          )}
          <div className='flex gap-2 mb-2'>
            <input ref={fileInputRef} type='file' onChange={handleFileSelect} accept='image/*,video/*,.pdf' className='hidden' />
            <Button type='button' onClick={() => fileInputRef.current?.click()} variant='outline' className='rounded-full' disabled={sending}>
              <Paperclip className='w-4 h-4' />
            </Button>
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder='Share free content with the community...'
              className='flex-1'
              rows={3}
              data-testid='community-post-input'
            />
          </div>
          <Button
            type='submit'
            disabled={sending || (!newMessage.trim() && !selectedFile)}
            className='bg-[#D4F246] text-[#1A3C34] hover:bg-[#E2F585] rounded-full'
            data-testid='post-community-btn'
          >
            {sending ? 'Posting...' : 'Post to Community'}
          </Button>
        </form>
      </Card>

      <Card className='bg-white rounded-2xl p-6'>
        <h3 className='text-lg font-bold text-[#1A3C34] mb-4'>Community Posts ({communityMessages.length})</h3>
        <div className='space-y-4 max-h-96 overflow-y-auto'>
          {communityMessages.map((msg, index) => (
            <div
              key={msg.id}
              className={`p-4 rounded-lg ${msg.is_pinned ? 'bg-[#D4F246]/20 border-2 border-[#D4F246]' : 'bg-gray-50'}`}
              data-testid={`community-post-${index}`}
            >
              <div className='flex justify-between items-start mb-2'>
                <div className='flex items-center gap-2'>
                  <span className='font-bold text-[#1A3C34]'>{msg.user_name}</span>
                  {msg.user_role === 'admin' && (
                    <span className='bg-[#D4F246] text-[#1A3C34] px-2 py-0.5 rounded-full text-xs font-bold'>COACH</span>
                  )}
                  {msg.is_pinned && <Pin className='w-4 h-4 text-[#1A3C34]' />}
                </div>
                <div className='flex gap-2'>
                  <Button size='sm' variant='ghost' onClick={() => handlePin(msg.id)} data-testid={`pin-${index}`}>
                    {msg.is_pinned ? 'Unpin' : 'Pin'}
                  </Button>
                  <Button size='sm' variant='ghost' className='text-red-600' onClick={() => handleDelete(msg.id)} data-testid={`delete-community-${index}`}>
                    <Trash2 className='w-4 h-4' />
                  </Button>
                </div>
              </div>
              <p className='text-gray-700 mb-2'>{msg.content}</p>
              {msg.file_url && (
                <div className='mt-2'>
                  {msg.file_type === 'image' && (
                    <img src={`${API}/messages/media/${msg.file_url}`} alt='Attachment' className='max-w-xs rounded cursor-pointer' onClick={() => window.open(`${API}/messages/media/${msg.file_url}`, '_blank')} />
                  )}
                  {msg.file_type === 'video' && (
                    <video controls className='max-w-xs rounded' src={`${API}/messages/media/${msg.file_url}`} />
                  )}
                  {msg.file_type === 'document' && (
                    <a href={`${API}/messages/media/${msg.file_url}`} target='_blank' rel='noopener noreferrer' className='text-blue-600 hover:underline text-sm'>
                      {msg.file_name}
                    </a>
                  )}
                </div>
              )}
              <p className='text-xs text-gray-500 mt-2'>{new Date(msg.created_at).toLocaleString()}</p>
            </div>
          ))}
          {communityMessages.length === 0 && (
            <div className='text-center py-8 text-gray-500'>No community posts yet. Be the first to share!</div>
          )}
        </div>
      </Card>
    </>
  );
}
